/**
 * Content Telemetry
 * ---------------------------------------------------------------
 * Persists generated simulation content to the database and
 * initializes a zeroed metrics row for every asset.
 *
 * Modular by design — the content generator stays pure; this
 * layer is the ONLY thing that touches Supabase. No analytics
 * UI lives here. No mock metrics. Strictly database-driven.
 */

import { supabase } from "@/integrations/supabase/client";
import type { SimulationContent } from "./contentGenerator";

export interface RegisteredAsset {
  /** DB id of the inserted content_assets row. */
  assetId: string;
  /** DB id of the linked zeroed content_metrics row. */
  metricsId: string;
  /** The generated content payload, untouched. */
  content: SimulationContent;
}

export interface RegisterOptions {
  /** Distribution platform tag — defaults to 'multi'. */
  platform?: string;
  /** Behavioral intelligence batch metadata (optional). */
  behavioralIntensity?: "low" | "medium" | "high";
  escalationPath?: string;
  scenarioSeed?: string;
  generationBatchId?: string;
}

/**
 * Initialize a zeroed metrics row for a freshly registered asset.
 * Pure DB call — no mock numbers, no client-side defaults beyond zero.
 */
export async function initializeContentMetrics(
  contentAssetId: string
): Promise<string> {
  const { data, error } = await supabase
    .from("content_metrics" as any)
    .insert({
      content_asset_id: contentAssetId,
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      saves: 0,
      ctr: 0,
      completion_rate: 0,
      avg_watch_time: 0,
    })
    .select("id")
    .single();

  if (error) throw error;
  return (data as unknown as { id: string }).id;
}

/**
 * Register a single generated SimulationContent in the telemetry
 * tables. Inserts content_assets, then a paired content_metrics
 * row. Returns both ids alongside the original content payload.
 */
export async function registerGeneratedContent(
  content: SimulationContent,
  opts: RegisterOptions = {}
): Promise<RegisteredAsset> {
  const platform = opts.platform ?? "multi";

  const { data: asset, error: assetErr } = await supabase
    .from("content_assets" as any)
    .insert({
      scenario_id: content.scenarioId,
      scenario_title: content.scenarioTitle,
      archetype: content.archetype,
      category: content.category,
      format: content.format,
      platform,
      hook: content.hook,
      cta: content.cta,
      status: "generated",
      render_status: "pending",
      behavioral_intensity: opts.behavioralIntensity ?? null,
      escalation_path: opts.escalationPath ?? null,
      scenario_seed: opts.scenarioSeed ?? null,
      generation_batch_id: opts.generationBatchId ?? null,
    })
    .select("id")
    .single();

  if (assetErr) throw assetErr;
  const assetId = (asset as unknown as { id: string }).id;

  const metricsId = await initializeContentMetrics(assetId);

  return { assetId, metricsId, content };
}

/**
 * Register an entire bundle (typically the 3-format output of
 * generateContentBundle). Runs inserts in parallel and returns
 * the registered assets in input order.
 */
export async function registerGeneratedBundle(
  bundle: SimulationContent[],
  opts: RegisterOptions = {}
): Promise<RegisteredAsset[]> {
  return Promise.all(bundle.map((c) => registerGeneratedContent(c, opts)));
}

/**
 * Log a single bundle_generated telemetry row to content_events.
 * content_asset_id is null because the event spans the whole bundle.
 * Fire-and-forget — never throws.
 */
export async function logBundleGenerated(payload: {
  scenarioId: string;
  scenarioTitle: string;
  formats: string[];
  assetIds: string[];
  generationTimeMs: number;
  platform?: string;
}): Promise<void> {
  try {
    await supabase.from("content_events" as any).insert({
      content_asset_id: null,
      event_type: "bundle_generated",
      metadata: payload as any,
    });
  } catch (e) {
    console.error("[contentTelemetry] bundle_generated log failed:", e);
  }
}

/** Log a failed bundle generation attempt. */
export async function logBundleGenerationFailed(payload: {
  scenarioId?: string;
  scenarioTitle?: string;
  formats: string[];
  error: string;
  generationTimeMs: number;
}): Promise<void> {
  try {
    await supabase.from("content_events" as any).insert({
      content_asset_id: null,
      event_type: "bundle_generation_failed",
      metadata: payload as any,
    });
  } catch (e) {
    console.error("[contentTelemetry] bundle_generation_failed log failed:", e);
  }
}

/**
 * Lightweight cross-component bus so the Generated Assets Queue
 * can refresh the moment a new bundle lands, without prop drilling.
 */
export const CONTENT_ASSETS_CHANGED_EVENT = "content-assets-changed";

export function emitContentAssetsChanged(detail?: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONTENT_ASSETS_CHANGED_EVENT, { detail }));
}
